import { startRegistration, startAuthentication } from '@simplewebauthn/browser';

type AuthSuccess = {
  token: string;
  user: any;
};

export type PendingTwoFA = {
  require2FA: true;
  pendingToken: string;
  user: { id: string; username: string };
};

export type PendingTwoFASetup = {
  require2FASetup: true;
  pendingSetupToken: string;
  user: { id: string; username: string };
};

export type LoginResult = AuthSuccess | PendingTwoFA;

const getApiBaseUrl = () => process.env.REACT_APP_API_URL || '/api';

const extractAuthData = (payload: any): AuthSuccess => {
  const data = payload?.data || payload;
  return {
    token: data?.token,
    user: data?.user,
  };
};

const beginRegistration = async (options: any) => {
  try {
    return await startRegistration({ optionsJSON: options } as any);
  } catch {
    return await startRegistration(options as any);
  }
};

const beginAuthentication = async (options: any) => {
  try {
    return await startAuthentication({ optionsJSON: options } as any);
  } catch {
    return await startAuthentication(options as any);
  }
};

export const registerWithPasskey = async (params: {
  username: string;
}): Promise<PendingTwoFASetup> => {
  const API_BASE_URL = getApiBaseUrl();

  const optionsResponse = await fetch(`${API_BASE_URL}/auth/passkeys/register/options`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  const optionsData = await optionsResponse.json();
  if (!optionsResponse.ok || !optionsData?.success) {
    throw new Error(optionsData?.error || 'Failed to initialize passkey signup');
  }

  const { registrationId, options } = optionsData.data;
  const credential = await beginRegistration(options);

  const verifyResponse = await fetch(`${API_BASE_URL}/auth/passkeys/register/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ registrationId, credential }),
  });

  const verifyData = await verifyResponse.json();
  if (!verifyResponse.ok || !verifyData?.success) {
    throw new Error(verifyData?.error || 'Passkey signup failed');
  }

  return {
    require2FASetup: true,
    pendingSetupToken: verifyData.pendingSetupToken,
    user: verifyData.user,
  };
};

export const loginWithPasskey = async (identifier?: string): Promise<LoginResult> => {
  const API_BASE_URL = getApiBaseUrl();

  const optionsResponse = await fetch(`${API_BASE_URL}/auth/passkeys/login/options`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier: identifier?.trim() || '' }),
  });

  const optionsData = await optionsResponse.json();
  if (!optionsResponse.ok || !optionsData?.success) {
    throw new Error(optionsData?.error || 'Failed to initialize passkey sign-in');
  }

  const { loginId, options } = optionsData.data;
  const credential = await beginAuthentication(options);

  const verifyResponse = await fetch(`${API_BASE_URL}/auth/passkeys/login/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ loginId, credential }),
  });

  const verifyData = await verifyResponse.json();
  if (!verifyResponse.ok || !verifyData?.success) {
    throw new Error(verifyData?.error || 'Passkey sign-in failed');
  }

  if (verifyData.require2FA) {
    return {
      require2FA: true,
      pendingToken: verifyData.pendingToken,
      user: verifyData.user,
    };
  }

  return extractAuthData(verifyData);
};
